import React from "react";
import { motion } from "framer-motion";
import {
  FaUsers,
  FaClock,
  FaCheckCircle,
  FaGlobe,
  FaMobileAlt,
  FaLock,
  FaChartPie,
  FaRocket,
} from "react-icons/fa";

const points = [
  {
    icon: <FaUsers size={34} />,
    title: "Experienced Cloud Engineers",
    desc: "Certified engineers with hands-on experience across AWS, Azure, and GCP.",
  },
  {
    icon: <FaClock size={34} />,
    title: "Fast & Reliable Support",
    desc: "Proactive monitoring, rapid response, and 24×7 operational support.",
  },
  {
    icon: <FaCheckCircle size={34} />,
    title: "Scalable & Cost-Effective",
    desc: "Optimized architectures that grow with your business while controlling costs.",
  },
  {
    icon: <FaGlobe size={34} />,
    title: "Serving Clients Globally",
    desc: "Trusted by startups and enterprises across multiple regions worldwide.",
  },
  {
    icon: <FaMobileAlt size={34} />,
    title: "Multi-Platform Integration",
    desc: "Seamless integration across web, mobile, and cloud-native platforms.",
  },
  {
    icon: <FaLock size={34} />,
    title: "Enterprise-Grade Security",
    desc: "IAM, encryption, compliance, and secure access management built-in.",
  },
  {
    icon: <FaChartPie size={34} />,
    title: "Data-Driven Insights",
    desc: "Analytics and monitoring to optimize performance and business outcomes.",
  },
  {
    icon: <FaRocket size={34} />,
    title: "Rapid Deployment",
    desc: "Agile cloud deployments with automation and best practices for speed.",
  },
];

const WhyChooseUs = () => {
  return (
    <section
      id="whyus"
      style={{
        position: "relative",
        padding: "50px 10px",
        background: "linear-gradient(180deg, #020617 0%, #0f172a 100%)",
        color: "#fff",
        overflow: "hidden",
        fontFamily: "'Roboto', sans-serif",
      }}
    >
      {/* Glow effects */}
      <div style={glow("#38bdf8", "15%", "25%")} />
      <div style={glow("#6366f1", "85%", "60%")} />

      <div style={{ maxWidth: "1300px", margin: "0 auto", position: "relative" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          style={{ textAlign: "center", marginBottom: "30px" }}
        >
          <h2 style={{ fontSize: "2.3rem", fontWeight: 800 }}>
            Why <span style={gradientText}>Choose Us</span>
          </h2>
          <p
            style={{
              color: "#c7d2fe",
              fontSize: "1.1rem",
              maxWidth: "720px",
              margin: "4px auto 0",
              lineHeight: "1.4",
            }}
          >
            We deliver enterprise-grade cloud solutions with reliability, security, and performance at the core.
          </p>
        </motion.div>

        {/* Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
          }}
        >
          {points.map((p, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.12 }}
              whileHover={{ y: -8 }}
              style={cardStyle}
            >
              <div style={iconWrap}>{p.icon}</div>
              <h3 style={{ fontSize: "1.2rem", marginBottom: "8px" }}>{p.title}</h3>
              <p style={{ color: "#94a3b8", fontSize: "0.9rem", lineHeight: "1.4" }}>{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Styles ---------- */

const cardStyle = {
  background: "rgba(15, 23, 42, 0.65)",
  backdropFilter: "blur(12px)",
  borderRadius: "18px",
  padding: "24px",
  border: "1px solid rgba(148,163,184,0.15)",
  boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
  minWidth: "250px", // ensures cards stay consistent
};

const iconWrap = {
  width: "48px",
  height: "48px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  background: "linear-gradient(135deg,#38bdf8,#6366f1)",
  marginBottom: "14px",
};

const glow = (color, left, top) => ({
  position: "absolute",
  width: "400px",
  height: "400px",
  background: color,
  filter: "blur(160px)",
  opacity: 0.2,
  left,
  top,
});

const gradientText = {
  background: "linear-gradient(90deg,#38bdf8,#6366f1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

export default WhyChooseUs;
